import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { complianceMonitorService } from '@/services/complianceMonitor/complianceMonitor.service';
import type { BottleneckRow } from '@/services/complianceMonitor/complianceMonitor.types';

function formatIsoDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

interface Props {
  limit?: number;
  refreshKey?: number;
}

export function BottlenecksPanel({ limit = 10, refreshKey }: Props) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<BottleneckRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    complianceMonitorService
      .getBottlenecks(limit)
      .then((res) => {
        if (cancelled) return;
        setRows(res.results || []);
      })
      .catch((err) => {
        console.error('[ComplianceMonitor] bottlenecks error:', err);
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [limit, refreshKey]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          {t('complianceMonitor.bottlenecks.title')}
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">
          {t('complianceMonitor.bottlenecks.subtitle')}
        </p>
      </div>

      {loading && (
        <div className="text-sm text-gray-500 py-4 text-center">{t('common.loading')}</div>
      )}

      {!loading && rows.length === 0 && (
        <div className="text-sm text-gray-500 py-4 text-center">
          {t('complianceMonitor.bottlenecks.empty')}
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-[720px] w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="py-2 pr-3 font-medium text-gray-700">
                  {t('complianceMonitor.bottlenecks.code')}
                </th>
                <th className="py-2 pr-3 font-medium text-gray-700">
                  {t('complianceMonitor.bottlenecks.name')}
                </th>
                <th className="py-2 pr-3 font-medium text-gray-700">
                  {t('complianceMonitor.bottlenecks.supplier')}
                </th>
                <th className="py-2 pr-3 font-medium text-gray-700 text-right">
                  {t('complianceMonitor.bottlenecks.leadTime')}
                </th>
                <th className="py-2 pr-3 font-medium text-gray-700 text-right">
                  {t('complianceMonitor.bottlenecks.totalSoBlocked')}
                </th>
                <th className="py-2 pr-3 font-medium text-amber-800 text-right">
                  {t('complianceMonitor.bottlenecks.urgent')}
                </th>
                <th className="py-2 pr-3 font-medium text-blue-700 text-right">
                  {t('complianceMonitor.bottlenecks.scheduleNext')}
                </th>
                <th className="py-2 pr-3 font-medium text-red-700 text-right">
                  {t('complianceMonitor.bottlenecks.notFeasible')}
                </th>
                <th className="py-2 pr-3 font-medium text-gray-700 text-right">
                  {t('complianceMonitor.bottlenecks.isBottleneck')}
                </th>
                <th className="py-2 pr-3 font-medium text-gray-700">
                  {t('complianceMonitor.bottlenecks.materialReadyMax')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.component_product_id}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="py-2 pr-3 font-mono text-gray-900">
                    {r.component_product_code || '—'}
                  </td>
                  <td className="py-2 pr-3 text-gray-900">
                    {r.component_product_name || '—'}
                  </td>
                  <td className="py-2 pr-3 text-gray-700">{r.supplier_name || '—'}</td>
                  <td className="py-2 pr-3 text-right text-gray-700 tabular-nums">
                    {r.lead_time_days || 0}
                  </td>
                  <td className="py-2 pr-3 text-right font-semibold text-gray-900 tabular-nums">
                    {r.total_so_blocked}
                  </td>
                  <td className="py-2 pr-3 text-right text-amber-800 tabular-nums">
                    {r.urgent_count}
                  </td>
                  <td className="py-2 pr-3 text-right text-blue-700 tabular-nums">
                    {r.schedule_next_week_count}
                  </td>
                  <td className="py-2 pr-3 text-right text-red-700 tabular-nums">
                    {r.not_feasible_count}
                  </td>
                  <td className="py-2 pr-3 text-right text-gray-700 tabular-nums">
                    {r.is_bottleneck_count}
                  </td>
                  <td className="py-2 pr-3 text-gray-700">
                    {formatIsoDate(r.material_ready_by_date_max)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
